import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge, Button, Card, Spinner, Alert, AlertDescription,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  EmptyState,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@agion/ui'
import { KeyRound, RefreshCw, Plus, Trash2, CheckCircle2 } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { formatDate } from '@/core/infra_utils'
import { listCredentials, deleteCredential, validateCredential } from './infra_credential.service'
import type { CloudCredential } from './infra_credential.service'
import { AddCredentialDialog } from './InfraAddCredentialDialog'

const statusVariant: Record<CloudCredential['status'], 'success' | 'warning' | 'error' | 'info'> = {
  active: 'success',
  rotating: 'info',
  expired: 'warning',
}

export function CredentialsPage() {
  const { t } = useTranslation(['pages'])
  const [credentials, setCredentials] = useState<CloudCredential[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [validatingId, setValidatingId] = useState<string | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<CloudCredential | null>(null)
  const [isRevoking, setIsRevoking] = useState(false)

  const loadCredentials = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await listCredentials()
      setCredentials(response.data ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credentials')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadCredentials()
  }, [loadCredentials])

  const handleValidate = async (id: string) => {
    setValidatingId(id)
    try {
      const result = await validateCredential(id)
      // Refresh to show updated last_validated_at
      if (result.valid) {
        void loadCredentials()
      }
    } catch {
      // Ignore — validation result shown inline
    } finally {
      setValidatingId(null)
    }
  }

  const handleRevoke = async () => {
    if (!revokeTarget) return
    setIsRevoking(true)
    try {
      await deleteCredential(revokeTarget.id)
      setRevokeTarget(null)
      void loadCredentials()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke credential')
    } finally {
      setIsRevoking(false)
    }
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('pages:credentials.title', 'Cloud Credentials')}
        description={t('pages:credentials.description', 'Manage cloud provider credentials stored securely in OpenBao')}
        icon={KeyRound}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => void loadCredentials()}>
              <RefreshCw size={14} className="mr-1" />
              {t('pages:credentials.refresh', 'Refresh')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setAddDialogOpen(true)}>
              <Plus size={14} className="mr-1" />
              {t('pages:credentials.add', 'Add Credential')}
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : credentials.length === 0 ? (
        <Card>
          <EmptyState
            icon={KeyRound}
            title={t('pages:credentials.empty.title', 'No credentials configured')}
            description={t('pages:credentials.empty.description', 'Add a cloud provider credential to get started with environment provisioning.')}
          />
        </Card>
      ) : (
        <Table variant="modern">
          <TableHeader variant="modern">
            <TableRow variant="modern">
              <TableHead variant="modern">{t('pages:credentials.columns.name', 'Name')}</TableHead>
              <TableHead variant="modern">{t('pages:credentials.columns.provider', 'Provider')}</TableHead>
              <TableHead variant="modern">{t('pages:credentials.columns.type', 'Type')}</TableHead>
              <TableHead variant="modern">{t('pages:credentials.columns.status', 'Status')}</TableHead>
              <TableHead variant="modern">{t('pages:credentials.columns.lastValidated', 'Last Validated')}</TableHead>
              <TableHead variant="modern">{t('pages:credentials.columns.created', 'Created')}</TableHead>
              <TableHead variant="modern">{t('pages:credentials.columns.actions', 'Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody variant="modern">
            {credentials.map(cred => (
              <TableRow key={cred.id} variant="modern">
                <TableCell className="font-medium">{cred.name}</TableCell>
                <TableCell>
                  <Badge variant="default">{cred.cloudProvider.toUpperCase()}</Badge>
                </TableCell>
                <TableCell className="text-slate-500 text-sm">{cred.credentialType}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant[cred.status]}>{cred.status}</Badge>
                </TableCell>
                <TableCell className="text-slate-500 text-sm">
                  {cred.lastValidatedAt ? formatDate(cred.lastValidatedAt) : '—'}
                </TableCell>
                <TableCell className="text-slate-500 text-sm">
                  {formatDate(cred.createdAt)}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleValidate(cred.id)}
                      disabled={validatingId === cred.id}
                    >
                      {validatingId === cred.id ? (
                        <Spinner className="h-3 w-3" />
                      ) : (
                        <CheckCircle2 size={14} className="mr-1" />
                      )}
                      {t('pages:credentials.validate', 'Validate')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRevokeTarget(cred)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 size={14} className="mr-1" />
                      {t('pages:credentials.revoke', 'Revoke')}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AddCredentialDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => void loadCredentials()}
      />

      <Dialog open={!!revokeTarget} onOpenChange={(isOpen) => { if (!isOpen) setRevokeTarget(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pages:credentials.revokeDialog.title', 'Revoke Credential')}</DialogTitle>
            <DialogDescription>
              {t('pages:credentials.revokeDialog.description', 'This will permanently revoke and delete the credential "{{name}}" from OpenBao. This action cannot be undone.', { name: revokeTarget?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={isRevoking}>
              {t('pages:credentials.revokeDialog.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={isRevoking}
              onClick={() => void handleRevoke()}
            >
              {isRevoking ? <Spinner className="h-4 w-4 mr-1" /> : <Trash2 size={14} className="mr-1" />}
              {t('pages:credentials.revokeDialog.confirm', 'Revoke')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
